import { getMomentTz } from "@dexteel/mesf-core";
import type { Moment } from "moment-timezone";
import {
	UTILITY_CONSUMPTION_DIRECTION,
	UTILITY_CONSUMPTION_MODE,
	UTILITY_CONSUMPTION_REPORT_MODE,
	type UtilityConsumptionDirection,
	type UtilityConsumptionMode,
	type UtilityConsumptionPeriod,
	type UtilityConsumptionReportCalendarMode,
	type UtilityConsumptionReportMode,
	type UtilityConsumptionReportRange,
} from "../models/UtilityConsumption";

const moment = getMomentTz();

const getPlantDate = (referenceDate: Date, plantTimeZone: string): Moment => {
	if (!moment.tz.zone(plantTimeZone)) {
		throw new Error(`Unknown plant timezone: ${plantTimeZone}`);
	}
	if (Number.isNaN(referenceDate.getTime())) {
		throw new Error("Utility consumption reference date must be valid.");
	}
	return moment.tz(referenceDate, plantTimeZone);
};

const getPeriodStart = (
	mode: UtilityConsumptionMode,
	referenceDate: Date,
	plantTimeZone: string,
): Moment => {
	const localDate = getPlantDate(referenceDate, plantTimeZone);
	switch (mode) {
		case UTILITY_CONSUMPTION_MODE.DAY:
			return localDate.startOf("month");
		case UTILITY_CONSUMPTION_MODE.WEEK:
			return localDate.startOf("day").day(0);
		case UTILITY_CONSUMPTION_MODE.MONTH:
			return localDate.startOf("year");
		case UTILITY_CONSUMPTION_MODE.DATE:
			return localDate.startOf("day");
	}
};

const getPeriodEnd = (mode: UtilityConsumptionMode, start: Moment): Moment => {
	switch (mode) {
		case UTILITY_CONSUMPTION_MODE.DAY:
			return start.clone().add(1, "month");
		case UTILITY_CONSUMPTION_MODE.WEEK:
			return start.clone().add(1, "week");
		case UTILITY_CONSUMPTION_MODE.MONTH:
			return start.clone().add(1, "year");
		case UTILITY_CONSUMPTION_MODE.DATE:
			return start.clone().add(1, "day");
	}
};

export const getUtilityConsumptionPeriod = (
	mode: UtilityConsumptionMode,
	referenceDate: Date,
	plantTimeZone: string,
): UtilityConsumptionPeriod => {
	const start = getPeriodStart(mode, referenceDate, plantTimeZone);
	const end = getPeriodEnd(mode, start);
	return {
		startUtc: start.clone().utc().toDate(),
		endUtc: end.utc().toDate(),
	};
};

export const getCurrentUtilityConsumptionPeriod = (
	mode: UtilityConsumptionMode,
	plantTimeZone: string,
): UtilityConsumptionPeriod =>
	getUtilityConsumptionPeriod(mode, new Date(), plantTimeZone);

export const moveUtilityConsumptionPeriod = (
	mode: UtilityConsumptionMode,
	referenceDate: Date,
	direction: UtilityConsumptionDirection,
	plantTimeZone: string,
): Date => {
	const amount = direction === UTILITY_CONSUMPTION_DIRECTION.PREVIOUS ? -1 : 1;
	const nextDate = getPlantDate(referenceDate, plantTimeZone);
	switch (mode) {
		case UTILITY_CONSUMPTION_MODE.DAY:
			nextDate.add(amount, "month");
			break;
		case UTILITY_CONSUMPTION_MODE.WEEK:
			nextDate.add(amount, "week");
			break;
		case UTILITY_CONSUMPTION_MODE.MONTH:
			nextDate.add(amount, "year");
			break;
		case UTILITY_CONSUMPTION_MODE.DATE:
			nextDate.add(amount, "day");
			break;
	}
	return nextDate.toDate();
};

/** Weeks run Sunday through Saturday (US convention), matching `getPeriodStart`. */
export const getUtilityConsumptionPeriodLabel = (
	mode: UtilityConsumptionMode,
	referenceDate: Date,
	plantTimeZone: string,
): string => {
	const start = moment.utc(referenceDate).tz(plantTimeZone);
	switch (mode) {
		case UTILITY_CONSUMPTION_MODE.DAY:
			return start.format("MMMM YYYY");
		case UTILITY_CONSUMPTION_MODE.WEEK: {
			const end = start.clone().add(6, "days");
			return `${start.format("MMM D")} - ${end.format(
				end.isSame(start, "month") ? "D, YYYY" : "MMM D, YYYY",
			)}`;
		}
		case UTILITY_CONSUMPTION_MODE.MONTH:
			return start.format("YYYY");
		case UTILITY_CONSUMPTION_MODE.DATE:
			return start.format("ddd, MMM D, YYYY");
	}
};

export const isUtilityConsumptionNextDisabled = (
	period: UtilityConsumptionPeriod,
	plantTimeZone: string,
): boolean => {
	const today = moment.tz(plantTimeZone).startOf("day");
	const start = moment.utc(period.startUtc).tz(plantTimeZone);
	const end = moment.utc(period.endUtc).tz(plantTimeZone);
	return start.isSameOrBefore(today) && end.isAfter(today);
};

const REPORT_DATE_FORMAT = "YYYY-MM-DD";

const getReportDate = (dateKey: string, plantTimeZone: string): Moment => {
	if (!moment.tz.zone(plantTimeZone)) {
		throw new Error(`Unknown plant timezone: ${plantTimeZone}`);
	}
	const date = moment.tz(dateKey, REPORT_DATE_FORMAT, true, plantTimeZone);
	if (!date.isValid()) {
		throw new Error("Report dates must use YYYY-MM-DD format.");
	}
	return date.startOf("day");
};

export const getDefaultUtilityConsumptionReportRange = (
	plantTimeZone: string,
): UtilityConsumptionReportRange => {
	const today = moment.tz(plantTimeZone).startOf("day");
	return {
		startDateKey: today.clone().subtract(7, "days").format(REPORT_DATE_FORMAT),
		endDateKey: today.clone().subtract(1, "day").format(REPORT_DATE_FORMAT),
	};
};

export const getDefaultUtilityConsumptionSectionRange = (
	plantTimeZone: string,
): UtilityConsumptionReportRange => {
	const today = moment.tz(plantTimeZone).startOf("day");
	return {
		startDateKey: today.clone().startOf("year").format(REPORT_DATE_FORMAT),
		endDateKey: today.format(REPORT_DATE_FORMAT),
	};
};

export const getUtilityConsumptionReportDate = (
	dateKey: string,
	plantTimeZone: string,
): Moment => getReportDate(dateKey, plantTimeZone);

export const getUtilityConsumptionReportDateError = (
	range: UtilityConsumptionReportRange,
	plantTimeZone: string,
): string | null => {
	try {
		getReportDate(range.startDateKey, plantTimeZone);
		getReportDate(range.endDateKey, plantTimeZone);
	} catch {
		return "Start and End must be valid dates.";
	}
	return null;
};

const reportCalendarDashboardModes: Record<
	UtilityConsumptionReportCalendarMode,
	UtilityConsumptionMode
> = {
	day: UTILITY_CONSUMPTION_MODE.DATE,
	week: UTILITY_CONSUMPTION_MODE.WEEK,
	month: UTILITY_CONSUMPTION_MODE.DAY,
	year: UTILITY_CONSUMPTION_MODE.MONTH,
};

const getUtilityConsumptionReportCalendarPeriod = (
	mode: UtilityConsumptionReportCalendarMode,
	referenceDate: Date,
	plantTimeZone: string,
): UtilityConsumptionPeriod =>
	getUtilityConsumptionPeriod(
		reportCalendarDashboardModes[mode],
		referenceDate,
		plantTimeZone,
	);

export const getUtilityConsumptionReportPeriod = (
	mode: UtilityConsumptionReportMode,
	referenceDate: Date,
	range: UtilityConsumptionReportRange,
	plantTimeZone: string,
): UtilityConsumptionPeriod => {
	if (
		mode !== UTILITY_CONSUMPTION_REPORT_MODE.PERIOD &&
		mode !== UTILITY_CONSUMPTION_REPORT_MODE.SECTION
	) {
		return getUtilityConsumptionReportCalendarPeriod(
			mode,
			referenceDate,
			plantTimeZone,
		);
	}

	const start = getReportDate(range.startDateKey, plantTimeZone);
	let end = getReportDate(range.endDateKey, plantTimeZone).add(1, "day");
	if (mode === UTILITY_CONSUMPTION_REPORT_MODE.SECTION) {
		const boundary = getPlantDate(referenceDate, plantTimeZone);
		if (end.isAfter(boundary)) end = boundary;
	}
	return {
		startUtc: start.clone().utc().toDate(),
		endUtc: end.utc().toDate(),
	};
};

export const getCurrentUtilityConsumptionReportDate = (
	mode: UtilityConsumptionReportCalendarMode,
	plantTimeZone: string,
): Date =>
	getUtilityConsumptionReportCalendarPeriod(mode, new Date(), plantTimeZone)
		.startUtc;

export const moveUtilityConsumptionReportDate = (
	mode: UtilityConsumptionReportCalendarMode,
	referenceDate: Date,
	direction: UtilityConsumptionDirection,
	plantTimeZone: string,
): Date => {
	return moveUtilityConsumptionPeriod(
		reportCalendarDashboardModes[mode],
		referenceDate,
		direction,
		plantTimeZone,
	);
};

export const getUtilityConsumptionReportPeriodLabel = (
	mode: UtilityConsumptionReportCalendarMode,
	referenceDate: Date,
	plantTimeZone: string,
): string =>
	getUtilityConsumptionPeriodLabel(
		reportCalendarDashboardModes[mode],
		referenceDate,
		plantTimeZone,
	);

export const getUtilityConsumptionReportBucketLabels = (
	starts: Date[],
	mode: UtilityConsumptionReportMode,
	plantTimeZone: string,
): string[] => {
	if (mode === UTILITY_CONSUMPTION_REPORT_MODE.YEAR) {
		return starts.map((start) =>
			moment.utc(start).tz(plantTimeZone).format("MMM"),
		);
	}

	if (mode !== UTILITY_CONSUMPTION_REPORT_MODE.DAY) {
		return starts.map((start) =>
			moment.utc(start).tz(plantTimeZone).format("MM/DD/YYYY"),
		);
	}

	const localTimes = starts.map((start) =>
		moment.utc(start).tz(plantTimeZone).format("HH:mm"),
	);
	return starts.map((start, index) => {
		const localTime = localTimes[index];
		const repeated =
			localTimes.indexOf(localTime) !== localTimes.lastIndexOf(localTime);
		return repeated
			? `${localTime} ${moment.utc(start).tz(plantTimeZone).format("Z")}`
			: localTime;
	});
};
